import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { TextInput } from "../../components/ui/Field";
import { DocMarks, EmptyState, PageTitle, Spinner } from "../../components/ui/misc";
import { Crumb, Page, PageBar } from "../../components/ui/PageBar";
import { useInventorySearch, useSearch } from "../../lib/queries";
import { InventoryLabel } from "./InventoryLabel";
import { Snippet } from "./Snippet";

export function SearchPage({ q }: { q: string }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(q);
  const { data: hits = [], isLoading, isFetched } = useSearch(q);
  const { data: inventory = [], isFetched: inventoryFetched } = useInventorySearch(q);
  const card = "block rounded-lg border border-line bg-surface px-4 py-3 hover:border-green";
  return (
    <>
      <PageBar>
        <Crumb current>Search</Crumb>
      </PageBar>
      <Page className="max-w-4xl">
        <PageTitle>{q ? `Results for “${q}”` : "Search"}</PageTitle>
        <search className="mb-6 block">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void navigate({ to: "/search", search: { q: draft.trim() } });
            }}
          >
            <TextInput
              aria-label="Search"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Client, document title or code, device name or serial number…"
            />
          </form>
        </search>
        {isLoading ? <Spinner label="Searching…" /> : null}
        {q.trim().length >= 2 && isFetched && inventoryFetched && hits.length === 0 && inventory.length === 0 ? (
          <EmptyState
            title={`No documents match “${q}”`}
            body="Try a code such as RB-, a serial number, a shorter word, or check the spelling."
          />
        ) : null}
        {hits.length > 0 ? (
          <section aria-label="Documents" className="mb-6">
            <h2 className="mb-2 text-lg font-semibold text-ink">Documents</h2>
            <ul className="grid gap-2">
              {hits.map((h) => (
                <li key={h.id}>
                  <Link to="/d/$ref" params={{ ref: h.id }} className={card}>
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-ink">{h.title}</span>
                      <DocMarks code={h.code} kind={h.kind} />
                      <span className="ml-auto text-xs text-ink-3">{h.space.name}</span>
                    </span>
                    <Snippet parts={h.snippet} />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {inventory.length > 0 ? (
          <section aria-label="Inventory">
            <h2 className="mb-2 text-lg font-semibold text-ink">Inventory</h2>
            <ul className="grid gap-2">
              {inventory.map((h) => (
                <li key={`${h.type}:${h.id}`}>
                  {h.type === "site" ? (
                    <Link to="/clients/$clientId/sites" params={{ clientId: h.clientId }} className={card}>
                      <InventoryLabel hit={h} />
                    </Link>
                  ) : (
                    <Link
                      to="/clients/$clientId/items/$itemId"
                      params={{ clientId: h.clientId, itemId: h.id }}
                      className={card}
                    >
                      <InventoryLabel hit={h} />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </Page>
    </>
  );
}
